import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { CampusSubject } from '../../enums/CampusSubject.js';
import type { ChancenwerkUser } from '../../master-data-management/users/entities/ChancenwerkUser.js';
import { CampusLastVisited } from '../entities/CampusLastVisited.js';
import { CampusSection } from '../entities/CampusSection.js';

const LAST_VISITED_MAX_ENTRIES = 3;

const filterOutChancenkurveLessonIDs = async (lessonIDs: string[]): Promise<string[]> => {
  if (!lessonIDs.length) {
    return lessonIDs;
  }

  const sectionCol = await getRawCollection<CampusSection>(CampusSection._collectionName);
  const chancenkurveSections = await sectionCol
    .find({
      subjects: CampusSubject.CHANCENKURVE,
      lessonIDs: { $in: lessonIDs },
    })
    .project({ lessonIDs: 1 })
    .toArray();

  const blockedLessonIDs = new Set(chancenkurveSections.flatMap(section => section.lessonIDs ?? []));
  return lessonIDs.filter(lessonID => !blockedLessonIDs.has(lessonID));
};

const filterChancenkurveLessonsFromLastVisited = async (lastVisited: CampusLastVisited): Promise<void> => {
  const allowedLessonIDs = await filterOutChancenkurveLessonIDs(lastVisited.lessonIDs ?? []);
  lastVisited.lessonIDs = allowedLessonIDs;
  lastVisited.lessons = lastVisited.lessons?.filter(lesson => allowedLessonIDs.includes(lesson._id));
};

const commitLastVisited = registerMutation({
  type: () => Boolean,
  operationName: 'commitLastVisited',
  resolve: (lessonID: string) => {
    return async () => {
      if (!lessonID || !lessonID.length) {
        throw new Error('Invalid lessonID');
      }

      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        _id: 1,
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const existing = await CampusLastVisited.getByID<CampusLastVisited>(user._id)({
        userID: 1,
        lessonIDs: 1,
      });

      if (!existing) {
        await new CampusLastVisited({
          userID: user._id,
          lessonIDs: [lessonID],
        }).create();
        return;
      }

      if (!existing.lessonIDs) {
        existing.lessonIDs = [];
      }

      existing.lessonIDs = [lessonID, ...existing.lessonIDs.filter(id => id !== lessonID)];
      if (existing.lessonIDs.length > LAST_VISITED_MAX_ENTRIES) {
        existing.lessonIDs = existing.lessonIDs.slice(0, LAST_VISITED_MAX_ENTRIES);
      }

      await existing.save();

      return true;
    };
  },
  options: {},
  params: [{ type: () => String, name: 'lessonID' }],
});

const getLastVisited = registerQuery({
  type: () => CampusLastVisited,
  operationName: 'getLastVisited',
  resolve: () => {
    return async (gqlFields: BaseQLSelectionSet<CampusLastVisited>) => {
      const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({
        _id: 1,
        roles: { name: 1, permissions: 1, contexts: 1 },
      });

      if (!user) {
        throw new Error('User not found');
      }

      const existing = await CampusLastVisited.getByID<CampusLastVisited>(user._id)({
        userID: 1,
        lessonIDs: 1,
        ...gqlFields,
      });

      if (!existing) {
        const n = new CampusLastVisited({
          userID: user._id,
          lessonIDs: [],
          lessons: [],
        });
        await n.create();
        return n;
      }

      await filterChancenkurveLessonsFromLastVisited(existing);

      return existing;
    };
  },
  options: {},
  params: [],
});

export namespace LastVisitedAPI {
  export const commit = commitLastVisited;
  export const get = getLastVisited;
}
